import {
    fetchEventSource,
    FetchEventSourceInit,
} from '@microsoft/fetch-event-source';
import { BaseError } from '@superbet-group/web.lib.utilities';

import { logger } from '@src/app/logger';
import { MeticulousSseUtils } from '@src/app/utils/meticulous/meticulousUtils';

import {
    DataEvent,
    OfferUpdateEvent,
    SyncronizableEvent,
    TimestampsMap,
} from './types';

export function areEventsSyncronizable(events: OfferUpdateEvent[]): events is SyncronizableEvent[] {
    return 'resourceId' in events[0];
}

export function isDataMessage(event: SyncronizableEvent): event is DataEvent {
    return ('data' in event);
}

export function shouldSyncResource(event: SyncronizableEvent, timestampsMap: TimestampsMap): boolean {
    if (isDataMessage(event)) {
        return false;
    }
    return event.timestamp === null
        || !timestampsMap[event.resourceId]
        || event.timestamp! > timestampsMap[event.resourceId];
}

export class SseFatalError extends BaseError {
    constructor(message?: string) {
        super(message);
    }
}

export class SseRetriableError extends BaseError {
    constructor(message?: string) {
        super(message);
    }
}

export async function safeFetchEventSource(sseEndpoint: string, fetchEventSourceInit: FetchEventSourceInit) {
    try {
        // When running in Meticulous replay environments, we want to allow Meticulous
        // to replay the events as per recordings, instead of really fetching them
        if (window.Meticulous?.isRunningAsTest) {
            MeticulousSseUtils.addMeticulousSseEventListeners(sseEndpoint, fetchEventSourceInit);
        } else {
            await fetchEventSource(sseEndpoint, fetchEventSourceInit);
        }
    } catch (error: any) {
        if (shouldLogError(error)) {
            logger.error(error);
        }
    }
}

export function updateTimestampsMap(events: SyncronizableEvent[], timestampsMap: TimestampsMap): TimestampsMap {
    let newTimestampsMap = { ...timestampsMap };
    events.forEach((event) => {
        if (!isDataMessage(event)) {
            // We only need to save timestamps from last stream, once we receive initial message we
            // clear the map and start adding timestamps from current stream
            newTimestampsMap = {};
        }
        // Timestamp can be null, indicating beginning of stream
        newTimestampsMap[event.resourceId] = event.timestamp ?? 0;
    });
    return newTimestampsMap;
}

export function shouldLogError(error: any): boolean {
    // errors related to network issues or aborting requests
    const genericErrorMessages = [
        'The user aborted a request.',
        'Failed to fetch',
        'network error',
        'Fetch is aborted',
        'Load failed',
    ];
    return !genericErrorMessages.includes(error.message);
}
